/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	createContext,
	type ReactNode,
	startTransition,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

import { jsx } from '@atlaskit/css';

import { clearSkillsCache } from '../insert-menu-provider/fetch-skills';

/**
 * Configuration for Rovo Skills features.
 * This type is designed to be extensible for future configuration needs.
 */
export type RovoSkillsConfig = {
	/**
	 * Tags identifying sets of skills and conversation actions.
	 * Used to fetch skill metadata from the backend.
	 */
	tags?: {
		skillRegistryTags?: string[];
	};
};

/**
 * Context for Rovo Skills configuration.
 */
const RovoSkillsConfigContext = createContext<RovoSkillsConfig | undefined>(undefined);

export type RovoSkillsInvalidation = {
	skillsRevision: number;
	invalidateSkillLibrary: () => void;
};

const fallbackInvalidation: RovoSkillsInvalidation = {
	skillsRevision: 0,
	// Outside RovoSkillsConfigProvider there is no revision state to bump, so this only clears
	// the vanilla insert-menu cache. Relay consumers need the provider to refetch by revision.
	invalidateSkillLibrary: clearSkillsCache,
};

const RovoSkillsInvalidationContext = createContext<RovoSkillsInvalidation>(fallbackInvalidation);

/**
 * Hook to access Rovo Skills configuration.
 *
 * @returns The Rovo Skills configuration, or undefined if used outside of RovoSkillsConfigProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const config = useRovoSkillsConfig();
 *   const tags = config?.tags;
 * }
 * ```
 */
export function useRovoSkillsConfig(): RovoSkillsConfig | undefined {
	return useContext(RovoSkillsConfigContext);
}

export function useRovoSkillsInvalidation(): RovoSkillsInvalidation {
	return useContext(RovoSkillsInvalidationContext);
}

type RovoSkillsConfigProviderProps = {
	/**
	 * Configuration for Rovo Skills features.
	 */
	config: RovoSkillsConfig;

	/**
	 * Child components that will have access to the configuration.
	 */
	children: ReactNode;
};

/**
 * Provider component that supplies Rovo Skills configuration to descendant components.
 *
 * This provider should be placed high in the component tree (e.g., at the AISmartButton level)
 * to make configuration available to all skill-related components.
 *
 * @example
 * ```tsx
 * <RovoSkillsConfigProvider config={{ tags }}>
 *   <YourComponents />
 * </RovoSkillsConfigProvider>
 * ```
 */
export function RovoSkillsConfigProvider({
	config,
	children,
}: RovoSkillsConfigProviderProps): ReactNode {
	const value = useMemo(() => config, [config]);
	const [skillsRevision, setSkillsRevision] = useState(0);
	const invalidateSkillLibrary = useCallback(() => {
		clearSkillsCache();
		startTransition(() => {
			setSkillsRevision((revision) => revision + 1);
		});
	}, []);
	const invalidationValue = useMemo(
		() => ({
			skillsRevision,
			invalidateSkillLibrary,
		}),
		[invalidateSkillLibrary, skillsRevision],
	);

	return (
		<RovoSkillsConfigContext.Provider value={value}>
			<RovoSkillsInvalidationContext.Provider value={invalidationValue}>
				{children}
			</RovoSkillsInvalidationContext.Provider>
		</RovoSkillsConfigContext.Provider>
	);
}
